import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Log } from "ng2-logger";
import { Model } from "@app/strain/config/gages/models/model.model";
import { StrainMeasurementImage } from "@app/strain/common/models/view-measurement-models/strain-measurement.image";
import { TranslateService } from "@ngx-translate/core";
import { DrawingCanvasComponent } from "@app/strain/common/components/drawing-canvas/drawing-canvas.component";
import { StrainImageModel } from '@app/strain/common/models/view-measurement-models/strain-image.model';
import { Gage } from "@app/strain/config/gages/models/gage.model";

@Component({
    selector: 'app-strain-gage-configuration-form',
    templateUrl: './strain-gage-configuration-form.component.html',
    styleUrls: ['./strain-gage-configuration-form.component.scss']
})
export class StrainGageConfigurationFormComponent implements OnInit, OnChanges {
    _logger = Log.create(StrainGageConfigurationFormComponent.name);

    @ViewChild('frontSideCanvas', { static: false }) frontSideCanvas: DrawingCanvasComponent;
    @ViewChild('backSideCanvas', { static: false }) backSideCanvas: DrawingCanvasComponent;

    @Input() isLoading = true;
    @Input() showValidations = false;
    @Input() gages: Gage[] = [];
    @Input() measurementImage: StrainMeasurementImage = null;

    @Output() save = new EventEmitter<any>();

    public form: UntypedFormGroup;
    frontSideArrows = [];
    backSideArrows = [];
    visibleImage = false;

    constructor(private fb: UntypedFormBuilder,
                private translate: TranslateService,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.initForm(!!this.measurementImage);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['measurementImage'] && !changes['measurementImage'].firstChange) {
            this.initForm(!!this.measurementImage);
        }
    }

    initForm(isEditMode: boolean): void {
        this.showValidations = false;
        this.visibleImage = false;
        this.isLoading = false;

        this.form = this.fb.group({
            nrOfGages: [1],
            imageFormControl: [false],
            gages: this.fb.array([]),
            frontSideImage: [null],
            backSideImage: [null]
        });

        if (isEditMode && this.measurementImage) {
            const gageCount = this.measurementImage.gages?.length || 1;
            this.form.setControl('nrOfGages', new UntypedFormControl(gageCount, Validators.required));
            this.measurementImage.gages.forEach(gage => {
                this.array('gages').push(this.buildRow(gage));
                this.updateArrows();
            });

            this.form.setControl('frontSideImage', new UntypedFormControl(
                this.measurementImage.frontSideImage && this.measurementImage.frontSideImage.data
                    ? this.measurementImage.frontSideImage : null
            ));
            this.form.setControl('backSideImage', new UntypedFormControl(
                this.measurementImage.backSideImage && this.measurementImage.backSideImage.data
                    ? this.measurementImage.backSideImage : null
            ));
            this.updateArrows();
        } else {
            this.addRow();
        }

        this.cdr.detectChanges();
    }

    addRow(): void {
        this.array('gages').push(this.buildRow());
        this.updateArrows();
        this.showValidations = false;
    }

    changeRows(value: number): void {
        const target = Number(value) || 0;
        const gagesArray = this.array('gages');
        const current = gagesArray.length;

        if (target > current) {
            for (let i = current; i < target; i++) {
                this.addRow();
            }
            return;
        }

        if (target < current) {
            for (let i = current - 1; i >= target; i--) {
                gagesArray.removeAt(i);
            }
            this.updateArrows();
        }
    }

    buildRow(item = null): UntypedFormGroup {
        if (!item) {
            return this.fb.group({
                gage: [null, Validators.required],
                name: [null, Validators.required],
                position: [false, Validators.required],
            });
        } else {
            return this.fb.group({
                gage: [item.gage, Validators.required],
                name: [item.name, Validators.required],
                position: [item.position, Validators.required],
            });
        }
    }

    updateArrows(): void {
        this.frontSideArrows = [];
        this.backSideArrows = [];

        this.form.get('gages').value.forEach((arrow, index) => {
            arrow.position
                ? this.backSideArrows.push({
                    index: index + 1,
                    gageType: arrow.gage ? arrow.gage.model : Model.Unidirectional,
                    gageName: arrow.name
                })
                : this.frontSideArrows.push({
                    index: index + 1,
                    gageType: arrow.gage ? arrow.gage.model : Model.Unidirectional,
                    gageName: arrow.name
                });
        });
    }

    updateGageType(type, index): void {
        this.backSideArrows.forEach((backArrow) => {
            if (backArrow.index === index + 1) {
                backArrow.gageType = type ? type.model : Model.Unidirectional;
            }
        });
        this.frontSideArrows.forEach((frontArrow) => {
            if (frontArrow.index === index + 1) {
                frontArrow.gageType = type ? type.model : Model.Unidirectional;
            }
        });
    }

    changeImage(): void {
        this.visibleImage = !this.visibleImage;
    }

    getFrontSideImage(): string {
        return this.measurementImage && this.measurementImage.frontSideImage ?
            this.measurementImage.frontSideImage.url : '';
    }

    getBackSideImage(): string {
        return this.measurementImage && this.measurementImage.backSideImage ?
            this.measurementImage.backSideImage.url : '';
    }

    control(name: string): UntypedFormControl {
        return this.form && this.form.get(name) as UntypedFormControl;
    }

    array(name: string): UntypedFormArray {
        return this.form && this.form.get(name) as UntypedFormArray;
    }

    arrayControl(index, name): UntypedFormControl {
        return (this.array('gages').controls[index] as UntypedFormArray).get(name) as UntypedFormControl;
    }

    valid(): boolean {
        return this.form && this.form.valid;
    }

    value() {
        if (this.form && this.form.value) {
            if (this.form.value.frontSideImage) {
                this.form.value.frontSideImage = this.convertToFile(this.form.value.frontSideImage, 'frontSideImage.jpg');
            }
            if (this.form.value.backSideImage) {
                this.form.value.backSideImage = this.convertToFile(this.form.value.backSideImage, 'backSideImage.jpg');
            }
        }

        if (this.form.value.gages) {
            this.form.value.gages = this.array('gages').controls.map((control) => {
                const gage = control.value;
                return {
                    ...gage,
                    position: gage.position ? 1 : 0
                };
            });
        }

        return this.form.value;
    }

    reset(): void {
        this._logger.info("resetForm:: Resetting gage configuration form");
        this.form.reset();
        this.frontSideCanvas?.onClearCanvas();
        this.backSideCanvas?.onClearCanvas();
        this.measurementImage = new StrainMeasurementImage();
    }

    convertToFile(imageForm, imageName) {
        if (imageForm instanceof StrainImageModel) {
            return imageForm;
        }

        return new StrainImageModel({
            name: imageName,
            contentType: 'image/jpeg',
            extension: '.jpg',
            data: imageForm.split(',')[1]
        });
    }

    onSave(): void {
        if (this.valid()) {
            this.showValidations = false;
            this.save.emit(this.value());
        } else {
            this.showValidations = true;
        }
    }
}
